// pages/BlogDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Share,
  ThumbUp,
  CalendarToday,
  Person,
  Label,
} from '@mui/icons-material';
import { db } from '../../lib/firebase';
import { doc, getDoc, updateDoc, increment ,deleteDoc} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { deleteFileByUrl } from '../../lib/githubUpload';

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlog();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBlog = async () => {
    try {
      const blogDoc = await getDoc(doc(db, 'blogs', id));
      if (blogDoc.exists()) {
        const data = blogDoc.data();
        setBlog({
          id: blogDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        });
        
        // Increment view count
        await updateDoc(doc(db, 'blogs', id), {
          views: increment(1)
        });
      } else {
        setError('Blog not found');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to load blog');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        // Extract and delete images from content
        const imgRegex = /<img[^>]+src="([^">]+)"/g;
        let match;
        while ((match = imgRegex.exec(blog.content)) !== null) {
          await deleteFileByUrl(match[1]);
        }
        
        // Delete cover image
        if (blog.coverImage) {
          await deleteFileByUrl(blog.coverImage);
        }
        
        // Delete from Firestore
        await deleteDoc(doc(db, 'blogs', id));
        navigate('/admin/blogs');
      } catch (err) {
        console.error('Error deleting blog:', err);
        alert('Failed to delete blog');
      }
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/blogs')} sx={{ mt: 2 }}>
          Back to Blogs
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/blogs')} sx={{ mb: 2 }}>
        Back to Blogs
      </Button>

      <Paper elevation={3} sx={{ overflow: 'hidden' }}>
        {blog.coverImage && (
          <Box
            component="img"
            src={blog.coverImage}
            alt={blog.title}
            sx={{
              width: '100%',
              height: 400,
              objectFit: 'cover',
            }}
          />
        )}

        <Box sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
            <Chip label={blog.category} color="primary" />
            <Box>
              <IconButton onClick={handleShare}>
                <Share />
              </IconButton>
              {currentUser && currentUser.uid === blog.userId && (
                <>
                  <IconButton onClick={() => navigate(`/admin/blogs/edit/${blog.id}`)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={handleDelete} color="error">
                    <Delete />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>

          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            {blog.title}
          </Typography>

          <Box display="flex" gap={3} mb={3} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              <Person fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {blog.author}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {blog.createdAt?.toLocaleDateString()}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <ThumbUp fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {blog.views || 0} views
              </Typography>
            </Box>
          </Box>

          {blog.tags && blog.tags.length > 0 && (
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Label fontSize="small" color="action" />
              <Stack direction="row" spacing={1}>
                {blog.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box
            sx={{
              '& h1': { fontSize: '2em', fontWeight: 'bold', margin: '0.67em 0' },
              '& h2': { fontSize: '1.5em', fontWeight: 'bold', margin: '0.83em 0' },
              '& h3': { fontSize: '1.17em', fontWeight: 'bold', margin: '1em 0' },
              '& p': { margin: '1em 0', lineHeight: 1.6 },
              '& ul, & ol': { margin: '1em 0', paddingLeft: '2em' },
              '& li': { margin: '0.5em 0' },
              '& blockquote': {
                margin: '1em 0',
                paddingLeft: '1em',
                borderLeft: '4px solid #1976d2',
                color: '#666',
                fontStyle: 'italic',
              },
              '& pre': {
                backgroundColor: '#f4f4f4',
                padding: '1em',
                borderRadius: '4px',
                overflow: 'auto',
                fontFamily: 'monospace',
              },
              '& code': {
                backgroundColor: '#f4f4f4',
                padding: '0.2em 0.4em',
                borderRadius: '3px',
                fontFamily: 'monospace',
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                margin: '1em 0',
              },
              '& a': {
                color: '#1976d2',
                textDecoration: 'underline',
              },
            }}
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default BlogDetail;